import React from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getIcon } from './iconRegistry'
import './WingsIcon.css'

/**
 * WingsIcon Component
 * 
 * A flexible icon component using Font Awesome with consistent sizing.
 * Supports all Font Awesome icon libraries (solid, regular, light, brands, duotone).
 * 
 * Icons can be referenced by name (recommended) or by importing Font Awesome objects.
 * 
 * @component
 * @example
 * // Basic usage with icon name (no imports needed!)
 * <WingsIcon icon="heart" size="medium" />
 * <WingsIcon icon="user" size="large" color="var(--w-color-teal-700)" />
 * <WingsIcon icon="github" size="mini" />
 * <WingsIcon icon="spinner" spin size="small" />
 * 
 * @example
 * // Or with Font Awesome icon object for custom/uncommon icons
 * import { faCustomIcon } from '@fortawesome/pro-light-svg-icons'
 * <WingsIcon icon={faCustomIcon} size="small" style={{ marginRight: '8px' }} />
 */
const WingsIcon = ({
  icon,
  size = 'medium',
  color,
  spin = false,
  pulse = false,
  fixedWidth = false,
  rotation,
  flip,
  className = '',
  style = {},
  ...props
}) => {
  // Resolve icon from registry if string, otherwise use as-is
  const resolvedIcon = getIcon(icon)
  
  // If icon not found, return null
  if (!resolvedIcon) {
    return null
  }

  // Build class names
  const classNames = [
    'w-icon',
    `w-icon-${size}`,
    className
  ].filter(Boolean).join(' ')

  // Build inline styles
  const iconStyle = {
    ...style,
    ...(color && { color })
  }

  return (
    <FontAwesomeIcon
      icon={resolvedIcon}
      className={classNames}
      style={iconStyle}
      spin={spin}
      pulse={pulse}
      fixedWidth={fixedWidth}
      rotation={rotation}
      flip={flip}
      {...props}
    />
  )
}

WingsIcon.propTypes = {
  /** Icon name (string) from icon registry or Font Awesome icon object */
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array
  ]).isRequired,
  
  /** Icon size matching button sizes */
  size: PropTypes.oneOf(['xl', 'large', 'medium', 'small', 'mini', 'tiny']),
  
  /** Icon color (CSS color value or custom property) */
  color: PropTypes.string,
  
  /** Rotate icon continuously */
  spin: PropTypes.bool,
  
  /** Rotate icon in 8 steps */
  pulse: PropTypes.bool,
  
  /** Set icon to fixed width for alignment */
  fixedWidth: PropTypes.bool,
  
  /** Rotate icon by degrees (90, 180, 270) */
  rotation: PropTypes.oneOf([90, 180, 270]),
  
  /** Flip icon horizontally or vertically */
  flip: PropTypes.oneOf(['horizontal', 'vertical', 'both']),
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** Inline styles */
  style: PropTypes.object
}

export default WingsIcon
